import { useState, useEffect, useCallback } from 'react'
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import api from '@/shared/api/client'
import { useAuthStore } from '@/shared/stores/auth.store'
import { resolveHomeRoute } from '@/shared/rbac'
import { prefetchHomeData } from '@/features/auth/lib/prefetch-home'
import { getSafeNext } from '@/features/auth/lib/safe-next'

export interface AuthLayoutContext {
  onLoginSuccess: (userName: string, homeRoute: string) => void
}

type Phase = 'idle' | 'check' | 'slide' | 'expand' | 'exit'

export function AuthLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user, isAuthenticated, isLoading } = useAuthStore()

  const [phase, setPhase] = useState<Phase>('idle')
  const [transitionName, setTransitionName] = useState('')
  const [specialtiesCount, setSpecialtiesCount] = useState<number | null>(null)

  useEffect(() => {
    api.get<{ specialties_count: number }>('/stats')
      .then((r) => setSpecialtiesCount(r.data.specialties_count))
      .catch(() => {})
  }, [])

  const onLoginSuccess = useCallback((userName: string, homeRoute: string) => {
    const destination = getSafeNext(location.search) ?? homeRoute
    setTransitionName(userName)
    prefetchHomeData(queryClient, destination)
    setPhase('check')
    setTimeout(() => setPhase('slide'), 800)
    setTimeout(() => setPhase('exit'), 1600)
    setTimeout(() => navigate(destination, { replace: true }), 2200)
  }, [navigate, queryClient, location.search])

  if (isLoading) return null
  if (isAuthenticated && phase === 'idle') {
    const destination = getSafeNext(location.search) ?? resolveHomeRoute(user?.permissions ?? [])
    return <Navigate to={destination} replace />
  }

  const isTransitioning = phase !== 'idle'

  return (
    <>
      <style>{`
        @keyframes authPanelSlideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-100%); opacity: 0; }
        }
        @keyframes authCheckGrow {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes authCheckDraw {
          from { stroke-dashoffset: 48; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes authPulseRing {
          0% { box-shadow: 0 0 0 0 rgba(25, 80, 198, 0.3); }
          70% { box-shadow: 0 0 0 24px rgba(25, 80, 198, 0); }
          100% { box-shadow: 0 0 0 0 rgba(25, 80, 198, 0); }
        }
        @keyframes authTextUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes authBarFill {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        @keyframes authFadeOut {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(0.98); }
        }
        @keyframes authGlideCenter {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-25vw); opacity: 1; }
        }
        @keyframes authGlideFade {
          from { transform: translateX(-25vw); opacity: 1; }
          to { transform: translateX(-25vw); opacity: 0; }
        }
        @keyframes authFormFadeOut {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-20px); }
        }
      `}</style>

      <div
        className="auth-shell"
        style={{
          display: 'flex',
          minHeight: '100vh',
          position: 'relative',
          animation: phase === 'exit' ? 'authFadeOut 0.6s ease-in forwards' : undefined,
        }}
      >
        {/* ── Left Branding Panel ── */}
        <div
          style={{
            flex: '0 0 50%',
            background: 'linear-gradient(160deg, #0e266c 0%, #1950C6 45%, #4876db 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '48px 56px',
            position: 'relative',
            overflow: 'hidden',
            // Slide out animation
            ...(phase === 'slide' || phase === 'expand' || phase === 'exit'
              ? {
                  animation: 'authPanelSlideOut 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards',
                }
              : {}),
          }}
        >
          {/* Subtle grid pattern overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)',
              backgroundSize: '32px 32px',
              pointerEvents: 'none',
            }}
          />

          {/* Logo */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 80 }}>
              <img
                src="/brand/medicodio-logo-white.png"
                alt="Medicodio"
                style={{ height: 32, width: 'auto', display: 'block' }}
              />
            </div>

            <h1
              style={{
                fontSize: 44,
                fontWeight: 800,
                color: '#ffffff',
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                fontFamily: 'var(--font-sans)',
                maxWidth: 440,
                marginBottom: 20,
              }}
            >
              where AI coding delivers
              <br />
              clinical precision
            </h1>
            <p
              style={{
                fontSize: 16,
                lineHeight: 1.7,
                color: 'rgba(255,255,255,0.7)',
                maxWidth: 420,
                fontFamily: 'var(--font-sans)',
              }}
            >
              An AI-native platform that transforms clinical documentation into
              accurate medical codes — with full transparency, structured learning,
              and a clear path to coding autonomy.
            </p>
          </div>

          {/* Stats strip */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div
              style={{
                display: 'flex',
                gap: 40,
                marginBottom: 40,
                paddingTop: 24,
                borderTop: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              {[
                { value: specialtiesCount !== null ? String(specialtiesCount) : '—', label: 'Specialties' },
                { value: "100K+", label: 'Charts Processed' },
                { value: '100%', label: 'HIPAA Compliant' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: '#ffffff',
                      fontFamily: 'var(--font-sans)',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'rgba(255,255,255,0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      marginTop: 2,
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div
              style={{
                padding: '20px 24px',
                borderRadius: 16,
                background: 'rgba(255,255,255,0.06)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.5,
                  marginBottom: 12,
                  fontFamily: 'var(--font-sans)',
                }}
              >
                "Every encounter is traceable from ingestion to write-back.
                Transparency is not an afterthought — it's the foundation."
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.4)',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                Product Philosophy — Medicodio Platform
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              paddingTop: 24,
              fontSize: 12,
              color: 'rgba(255,255,255,0.35)',
              fontFamily: 'var(--font-sans)',
              letterSpacing: '0.01em',
            }}
          >
            &copy; {new Date().getFullYear()} Medicodio. All rights reserved.
          </div>
        </div>

        {/* ── Right Form Panel ── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--sp-6)',
            background: 'var(--bg)',
            transition: 'flex 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            // Scroll only for the form (which can be tall — mfa-setup).
            // SuccessContent animation needs visible overflow for its
            // slide/expand transform, so we gate scroll on !isTransitioning.
            overflowY: isTransitioning ? 'visible' : 'auto',
            maxHeight: '100vh',
          }}
        >
          <div style={{ maxWidth: 420, width: '100%', margin: isTransitioning ? undefined : 'auto 0' }}>
            {/* When transitioning, show success UI instead of form */}
            {isTransitioning ? (
              <SuccessContent
                name={transitionName}
                phase={phase}
              />
            ) : (
              <Outlet context={{ onLoginSuccess } satisfies AuthLayoutContext} />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// ── Success animation content ──

function SuccessContent({ name, phase }: { name: string; phase: Phase }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        ...(phase === 'slide'
          ? { animation: 'authGlideCenter 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards' }
          : phase === 'exit'
            ? { animation: 'authGlideFade 0.6s ease-in forwards' }
            : {}),
      }}
    >
      {/* Animated check circle */}
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--primary-400), var(--primary-700))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 32,
          animation: 'authCheckGrow 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), authPulseRing 1.2s ease-out 0.4s',
        }}
      >
        <svg width="36" height="36" viewBox="0 0 32 32" fill="none">
          <path
            d="M8 16.5L13.5 22L24 11"
            stroke="#fff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="48"
            style={{
              strokeDashoffset: 48,
              animation: 'authCheckDraw 0.5s ease-out 0.25s forwards',
            }}
          />
        </svg>
      </div>

      {/* Welcome text */}
      <div
        style={{
          textAlign: 'center',
          animation: 'authTextUp 0.5s ease-out 0.35s both',
        }}
      >
        <h2
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-sans)',
            marginBottom: 8,
            letterSpacing: '-0.02em',
          }}
        >
          Welcome back, {name}
        </h2>
        <p
          style={{
            fontSize: 14,
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {phase === 'check' && 'Authenticated successfully'}
          {phase === 'slide' && 'Loading your workspace...'}
          {(phase === 'expand' || phase === 'exit') && 'Taking you there now...'}
        </p>
      </div>

      {/* Progress bar */}
      <div
        style={{
          width: 200,
          height: 4,
          borderRadius: 2,
          background: 'var(--surface-0)',
          marginTop: 40,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 2,
            background: 'linear-gradient(90deg, var(--primary-300), var(--primary))',
            transformOrigin: 'left',
            transform: 'scaleX(0)',
            animation: 'authBarFill 2.2s cubic-bezier(0.4, 0, 0.2, 1) forwards',
          }}
        />
      </div>

      {/* Decorative dots */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          marginTop: 24,
          animation: 'authTextUp 0.5s ease-out 0.6s both',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--primary-200)',
              animation: `authDotPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
            }}
          />
        ))}
        <style>{`
          @keyframes authDotPulse {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.3); }
          }
        `}</style>
      </div>
    </div>
  )
}
